/**
 * Actions History Chart Component
 * Gráfico de linha mostrando tendência de ações ao longo do tempo
 */

import React from 'react';
import { Card, CardContent, Typography, Box, CircularProgress, Alert } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { format, parseISO } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

interface ActionsHistoryChartProps {
  data: Array<{
    date: string;
    total: number;
    successful: number;
    failed: number;
    successRate: number;
  }>;
  loading?: boolean;
  error?: string | null;
}

export const ActionsHistoryChart: React.FC<ActionsHistoryChartProps> = ({
  data,
  loading,
  error
}) => {
  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  const chartData = {
    labels: data.map(item => format(parseISO(item.date), 'dd/MM', { locale: ptBR })),
    datasets: [
      {
        label: 'Total de Ações',
        data: data.map(item => item.total),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y'
      },
      {
        label: 'Ações Bem-sucedidas',
        data: data.map(item => item.successful),
        borderColor: 'rgb(54, 162, 235)',
        backgroundColor: 'rgba(54, 162, 235, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y'
      },
      {
        label: 'Ações com Falha',
        data: data.map(item => item.failed),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.1)',
        fill: true,
        tension: 0.4,
        yAxisID: 'y'
      },
      {
        label: 'Taxa de Sucesso (%)',
        data: data.map(item => item.successRate),
        borderColor: 'rgb(153, 102, 255)',
        backgroundColor: 'rgba(153, 102, 255, 0.1)',
        fill: false,
        tension: 0.4,
        yAxisID: 'y1',
        borderDash: [5, 5]
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false
    },
    plugins: {
      legend: {
        position: 'top' as const
      },
      title: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.dataset.label === 'Taxa de Sucesso (%)') {
              label += context.parsed.y + '%';
            } else {
              label += context.parsed.y;
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        title: {
          display: true,
          text: 'Quantidade de Ações'
        }
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        title: {
          display: true,
          text: 'Taxa de Sucesso (%)'
        },
        grid: {
          drawOnChartArea: false
        },
        max: 100
      }
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Tendência de Ações ao Longo do Tempo
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Acompanhe o volume de ações, sucessos, falhas e taxa de sucesso para identificar padrões e
          anomalias
        </Typography>
        <Box sx={{ height: 400 }}>
          <Line data={chartData} options={options} />
        </Box>
      </CardContent>
    </Card>
  );
};
